import React from 'react';
import LocationHierarchy from './LocationHierarchy';

interface ZoneCardProps {
  zone: any;
  onEdit: (zone: any) => void;
  onDelete: (zone: any) => void;
  onBulkPhotos?: (zone: any) => void;
  isSelected?: boolean;
  onToggleSelection?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canBulk?: boolean;
}

const ZoneCard: React.FC<ZoneCardProps> = ({ 
  zone, 
  onEdit, 
  onDelete,
  onBulkPhotos,
  isSelected = false, 
  onToggleSelection,
  canEdit = false,
  canDelete = false,
  canBulk = false
}) => {
  const getLocationData = () => {
    return {
      site: zone.piece?.batiment?.site,
      batiment: zone.piece?.batiment,
      piece: zone.piece,
      zone: zone,
      box: null
    };
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
      {/* En-tête avec case à cocher, code, thème et actions */}
      <div className="flex items-start gap-2 mb-2">
        {/* Case à cocher à gauche */}
        {onToggleSelection && canBulk && (
          <label className="flex-shrink-0 cursor-pointer mt-0.5">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelection}
              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        )}
        
        {/* Contenu principal */}
        <div className="flex items-start justify-between gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate">{zone.code}</h3>
            {zone.theme && (
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 flex-shrink-0">
                {zone.theme}
              </span>
            )}
          </div>
          {(canEdit || canDelete || onBulkPhotos) && (
            <div className="flex gap-1 flex-shrink-0">
              {onBulkPhotos && (
                <button
                  onClick={() => onBulkPhotos(zone)}
                  className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                  title="Ajouter des objets par photos"
                >
                  📷
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => onEdit(zone)}
                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                  title="Modifier"
                >
                  ✏️
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => onDelete(zone)}
                  className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                  title="Supprimer"
                >
                  🗑️
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nom */}
      <p className="text-sm text-gray-700 mb-2 font-medium">{zone.nom}</p>

      {/* Localisation - Version verticale avec icônes */}
      <div className="space-y-0.5 mb-2">
        {zone.piece?.batiment?.site && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">📍</span>
            <span className="truncate">{zone.piece.batiment.site.nom}</span>
          </div>
        )}
        {zone.piece?.batiment && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">🏗️</span>
            <span className="truncate">{zone.piece.batiment.nom}</span>
          </div>
        )}
        {zone.piece && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">🚪</span>
            <span className="truncate">{zone.piece.nom}</span>
          </div>
        )}
      </div>

      {/* Statistiques */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span>📦</span>
          <span>{zone.objets?.length || 0}</span>
        </span>
        {(zone.homeBoxes?.length || 0) > 0 && (
          <span className="flex items-center gap-1">
            <span>🏠</span>
            <span>{zone.homeBoxes?.length}</span>
          </span>
        )}
        {(zone.currentBoxes?.length || 0) > 0 && (
          <span className="flex items-center gap-1">
            <span>📍</span>
            <span>{zone.currentBoxes?.length}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default ZoneCard;
